import {
  formatUnits,
  getPublicClient,
  parseUnits,
  readContract,
  writeContractAndWait,
} from "../viem";
import type { Address, Hex } from "viem";
import config, { CHAIN } from "../../config/config";
import { ChainType } from "../../constants/enum/enum";
import MafiaBulletAbi from "../../abis/MafiaBullet.json";

const contractAddress = config.inGameBulletAddress;
const abi = MafiaBulletAbi;
const WITHDRAW_TAX_UNITS_PER_PERCENT = 10;
const EIP_1967_IMPLEMENTATION_SLOT =
  "0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc" as Hex;
const LEGACY_BNB_IMPLEMENTATION =
  "0xa132a6a956ed1ba667f13046740261ad22a4eba3";
const LEGACY_BNB_WITHDRAW_TAX_SLOT =
  "0x000000000000000000000000000000000000000000000000000000000000006b" as Hex;

export const balanceOfBullet = async (
  address: string,
  signMsg: string,
  signature: string
) => {
  const balance = await readContract<string>({
    address: contractAddress,
    abi,
    functionName: "balanceOf",
    args: [address, JSON.stringify(signMsg), signature],
  });
  const decimal = await readContract<string>({
    address: contractAddress,
    abi,
    functionName: "decimals",
  });
  return formatUnits(balance, decimal);
};

export const getBulletAllowance = async (from: string, to: string) => {
  const decimals = Number(
    await readContract<string>({
      address: contractAddress,
      abi,
      functionName: "decimals",
    })
  );
  const data = await readContract<string>({
    address: contractAddress,
    abi,
    functionName: "allowances",
    args: [from, to],
  });
  return Number(formatUnits(data, decimals.toString()));
};

export const getBulletWithdrawTaxPercent = async () => {
  let withdrawTax: string;

  try {
    withdrawTax = await readContract<string>({
      address: contractAddress,
      abi,
      functionName: "withdrawTax",
    });
  } catch (error) {
    if (CHAIN !== ChainType.BNB) {
      throw error;
    }

    const client = getPublicClient();
    const implementation = await client.getStorageAt({
      address: contractAddress as Address,
      slot: EIP_1967_IMPLEMENTATION_SLOT,
    });
    const implementationAddress = implementation
      ? `0x${implementation.slice(-40)}`.toLowerCase()
      : "";

    // This BNB implementation stores withdrawTax privately, so its proxy
    // storage is the only read path. Restrict the fallback to that exact
    // implementation so a future upgrade cannot silently use a stale slot.
    if (implementationAddress !== LEGACY_BNB_IMPLEMENTATION) {
      throw error;
    }

    const storedWithdrawTax = await client.getStorageAt({
      address: contractAddress as Address,
      slot: LEGACY_BNB_WITHDRAW_TAX_SLOT,
    });
    if (storedWithdrawTax === undefined) {
      throw error;
    }
    withdrawTax = BigInt(storedWithdrawTax).toString();
  }

  return Number(withdrawTax) / WITHDRAW_TAX_UNITS_PER_PERCENT;
};

export const approveBullet = async (
  amount: number,
  to: string,
  account: string
) => {
  const decimals = Number(
    await readContract<string>({
      address: contractAddress,
      abi,
      functionName: "decimals",
    })
  );

  const data = await writeContractAndWait({
    address: contractAddress,
    abi,
    functionName: "approve",
    args: [to, parseUnits(Number(amount).toFixed(decimals), decimals)],
    account,
  });

  return data;
};

export const withdrawBullet = async (amount: number, account: string) => {
  const decimals = Number(
    await readContract<string>({
      address: contractAddress,
      abi,
      functionName: "decimals",
    })
  );
  const data = await writeContractAndWait({
    address: contractAddress,
    abi,
    functionName: "withdrawBullet",
    args: [parseUnits(Number(amount).toFixed(decimals), decimals)],
    account,
  });
  return data;
};
